import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  LifeBuoy, Monitor, Package, Calendar, ArrowLeft, Send,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/App";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEPARTMENTS = ["IT", "Logistics", "Security", "Catering", "Medical", "OfficeSupply"];
const PRIORITIES = ["Low", "Medium", "High", "Urgent"];

export default function RequestServiceNewRequest() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const presetDept = searchParams.get("department") || "";

  const [step, setStep] = useState(presetDept ? 2 : 1);
  const [catalog, setCatalog] = useState([]);
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    department: presetDept,
    service_type: "",
    priority: "Medium",
    description: "",
    requires_inventory: false,
  });

  useEffect(() => {
    if (form.department) {
      setLoadingCatalog(true);
      const token = localStorage.getItem("token");
      axios
        .get(`${API}/request/catalog?department=${form.department}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((res) => setCatalog(res.data))
        .catch(() => toast.error("Failed to load service catalog"))
        .finally(() => setLoadingCatalog(false));
    }
  }, [form.department]);

  const handleSubmit = async () => {
    if (!form.department || !form.service_type || !form.description) {
      toast.error("Please fill in all required fields");
      return;
    }
    setSubmitting(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/request/requests`,
        {
          department: form.department,
          service_type: form.service_type,
          priority: form.priority,
          description: form.description,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Service request submitted successfully!");
      navigate("/request-service/my-requests");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit request");
    }
    setSubmitting(false);
  };

  return (
    <div className="p-6 max-w-3xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate("/request-service/dashboard")}
          className="flex items-center gap-2 text-slate-500 hover:text-slate-700 mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm">Back to Dashboard</span>
        </button>
        <h1 className="font-['Barlow_Condensed'] text-3xl font-bold text-slate-800">
          New Service Request
        </h1>
        <p className="text-slate-500 mt-1">Fill in the details to submit a request</p>
      </div>

      {/* Step Indicator */}
      <div className="flex items-center gap-2 mb-8">
        {[1, 2, 3].map((s) => (
          <div key={s} className="flex items-center gap-2">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step >= s
                  ? "bg-teal-500 text-white"
                  : "bg-slate-200 text-slate-400"
              }`}
            >
              {s}
            </div>
            <span className={`text-sm ${step >= s ? "text-teal-700" : "text-slate-400"}`}>
              {s === 1 ? "Department" : s === 2 ? "Service Type" : "Details"}
            </span>
            {s < 3 && <div className="w-8 h-px bg-slate-300" />}
          </div>
        ))}
      </div>

      {/* Step 1: Department */}
      {step === 1 && (
        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
          <h2 className="font-['Barlow_Condensed'] text-xl font-bold text-slate-800 mb-4">
            Choose a Department
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {DEPARTMENTS.map((dept) => {
              const icons = {
                IT: Monitor,
                Logistics: Package,
                Security: LifeBuoy,
                Catering: LifeBuoy,
                Medical: Calendar,
                OfficeSupply: Package,
              };
              const colors = {
                IT: "from-blue-500 to-blue-600",
                Logistics: "from-amber-500 to-orange-600",
                Security: "from-red-500 to-red-600",
                Catering: "from-green-500 to-emerald-600",
                Medical: "from-cyan-500 to-teal-600",
                OfficeSupply: "from-purple-500 to-violet-600",
              };
              const Icon = icons[dept];
              return (
                <button
                  key={dept}
                  onClick={() => {
                    setForm({ ...form, department: dept, service_type: "" });
                    setStep(2);
                  }}
                  className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm hover:shadow-md transition-all text-center group"
                >
                  <div
                    className={`w-12 h-12 bg-gradient-to-br ${colors[dept]} rounded-lg flex items-center justify-center mx-auto mb-2`}
                  >
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <p className="font-medium text-slate-700">
                    {dept === "OfficeSupply" ? "Office Supply" : dept}
                  </p>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Step 2: Service Type */}
      {step === 2 && (
        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
          <h2 className="font-['Barlow_Condensed'] text-xl font-bold text-slate-800 mb-4">
            Select Service Type — {form.department}
          </h2>
          {loadingCatalog ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-teal-500" />
            </div>
          ) : catalog.length === 0 ? (
            <p className="text-slate-500 text-center py-8">No services available for this department.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {catalog.map((svc, idx) => (
                <button
                  key={idx}
                  onClick={() => {
                    setForm({
                      ...form,
                      service_type: svc.service_name,
                      requires_inventory: svc.requires_inventory,
                    });
                    setStep(3);
                  }}
                  className={`text-left p-4 rounded-lg border transition-all ${
                    form.service_type === svc.service_name
                      ? "border-teal-500 bg-teal-50"
                      : "border-slate-200 hover:border-teal-300 hover:bg-app"
                  }`}
                >
                  <p className="font-medium text-slate-800">{svc.service_name}</p>
                  {svc.requires_inventory && (
                    <p className="text-xs text-amber-600 mt-1">Requires hardware allocation</p>
                  )}
                </button>
              ))}
            </div>
          )}
          <div className="mt-4">
            <Button variant="ghost" onClick={() => setStep(1)}>
              ← Back to departments
            </Button>
          </div>
        </div>
      )}

      {/* Step 3: Details */}
      {step === 3 && (
        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
          <h2 className="font-['Barlow_Condensed'] text-xl font-bold text-slate-800 mb-4">
            Request Details
          </h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Department</label>
              <Input value={form.department} disabled />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Service Type</label>
              <Input value={form.service_type} disabled />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Priority <span className="text-red-500">*</span>
              </label>
              <Select
                value={form.priority}
                onValueChange={(v) => setForm({ ...form, priority: v })}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRIORITIES.map((p) => (
                    <SelectItem key={p} value={p}>
                      {p}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Description <span className="text-red-500">*</span>
              </label>
              <Textarea
                placeholder="Describe what you need..."
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={5}
              />
            </div>
            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={() => setStep(2)}>
                ← Back
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={submitting}
                className="bg-teal-500 hover:bg-teal-600 text-white"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Submit Request
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
